import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import AttToken from '../../helpers/attToken';
import Checklists from '../../service/Checklists';
import Clients from '../../service/Clients';
import { toast } from 'react-toastify';
import { Skeleton, Stack } from '@mui/material';
import Anonimo from '../../service/Anonimo';
import CheckMandatory from './CheckMandatorys';
import { trackEventMatomo } from '../../helpers/matomo';
import queryClient from '../../service/query';
import { UserContext } from '../../context/UserContext';
import { SpanColored } from './FormFillingStyle';
import AutocompleteSApi from '../AutocompleteSApi/AutocompleteSApi';
import NewFormFilling from '../NewFormFilling/Structure/NewFormFilling';
import QueryFn from '../../helpers/QueryFn';
import HeaderModal from '../HeaderModal/HeaderModal';
import FooterModal from '../FooterModal/FooterModal';

const clientsService = new Clients();
const serviceAnonimo = new Anonimo();
const queryFn = new QueryFn();

type props = {
  formId: string | null,
  setFormId: Function,
  open: boolean,
  setOpen: Function
};
const checkListService = new Checklists();

export default function FormFillingModal({ formId, setFormId, open, setOpen }: props) {
  const [selectedCliente, setSelectedCliente] = useState<string | null>(null);
  const [selectedLocal, setSelectedLocal] = useState<string | null>(null);
  const [selectedConjunto, setSelectedConjunto] = useState<string | null>(null);
  const [selectedUnidade, setSelectedUnidade] = useState<string | null>(null);
  const { hierarchyLabel } = useContext(UserContext);


  const INICIO = Date.now();

  const { t } = useTranslation('translation');

  const results = queryFn.useFetchMultiple([
    {
      key: 'FORM_FOR_FILLING',
      fn: async () => {
        try {
          const token = await AttToken();
          if (token) {
            const response = await checkListService.getChecklistForWebFilling(token, formId as string, false);
            return response.formToFilling;
          }
        } catch (error) {
          console.error('Error fetching form for filling:', error);
        }
      }
    },
    {
      key: 'GET_HIERARCHY_LIST',
      fn: async () => {
        try {
          const token = await AttToken();
          if (token) {
            const response = await clientsService.getListForWebForm(token, formId as string);
            return response;
          }
        } catch (error) {
          console.error('Error fetching hierarchy list:', error);
        }
      }
    }
  ]);

  const isLoading = results?.some((result: any) => result.isLoading);
  const isFetching = results?.some((result: any) => result.isFetching);

  const mutate = queryFn.useMutate('EXECUTA-ANONIMO',
    async () => {
      const response = await toast.promise(async () => {
        const token = await AttToken();
        if (token && selectedCliente && selectedLocal && selectedConjunto && selectedUnidade) {
          const hierarquia = {
            cliente: selectedCliente,
            local: selectedLocal,
            conjunto: selectedConjunto,
            unidade: selectedUnidade,
          };
          const response = await serviceAnonimo.executaChecklistLogado(token, hierarquia, { ...results[0].data as any, inicio: INICIO });
          setFormId(null);
          return response;
        }
      }, {
        success: t('Filling.formExecutedSuccessfully'),
        pending: t('Filling.executingForm'),
        error: t('Filling.formExecutionFailed')
      });
      return response;
    });

  useEffect(() => {
    if (selectedCliente === undefined) {
      setSelectedLocal(null);
      setSelectedConjunto(null);
      setSelectedUnidade(null);
    } if (selectedLocal === undefined) {
      setSelectedConjunto(null);
      setSelectedUnidade(null);
    } if (selectedConjunto === undefined) {
      setSelectedUnidade(null);
    }
  }, [selectedCliente, selectedLocal, selectedConjunto, selectedUnidade]);

  const close = () => {
    setOpen(false);
    setFormId(null);
    queryClient.removeQueries({ queryKey: ['FORM_FOR_FILLING'] });
    queryClient.removeQueries({ queryKey: ['GET_HIERARCHY_LIST'] });
  };

  const escape = (e: any) => {
    if (e.key === 'Escape') {
      close();
    }
  };

  useEffect(() => {
    document.addEventListener('keydown', escape, true);
  }, [escape]);

  const submit = queryFn.useMutate('EXECUTA-ANONIMO',
    async () => {
      try {
        trackEventMatomo('Filling.formFillingModal', 'click', 'button', 'salva');
        const result = CheckMandatory((results[0].data as any), t);
        if (!result.success) {
          if (result.chamado) toast.warn(`${t('Filling.formFilling.openTicketSubitem')} "${result.subItem}"${t('Filling.formFilling.insideItem')}"${result.item}"`);
          else if (result.texto) {
            toast.warn(result.texto);
          }
          else if (result.item === 'chamado') toast.warn(`${t('Filling.openTicketToCompleteForm')}`);
          else toast.warn(`${t('Filling.fillSubitens.subitem')} "${result.subItem}${t('Filling.notFilledWithinItem')}${result.item}"`);
        } else {
          setOpen(false);
          mutate.mutateAsync('');
          localStorage.removeItem('inicio');
          queryClient.removeQueries({ queryKey: ['FORM_FOR_FILLING'] });
          queryClient.removeQueries({ queryKey: ['GET_HIERARCHY_LIST'] });
          setFormId(null);
        }
      } catch (error) {
        console.error('Error during form submission:', error);
      }
    });

  return (
    <div className={`modal ${open ? "modal is-active" : "modal"}`}>
      <div className="modal-background" onClick={close} onKeyDown={(e) => escape} />
      <div className="modal-card" onSubmit={(e: React.FormEvent) => e.preventDefault()}>
        <HeaderModal headerTitle={t('Filling.fillForm')} close={close} />
        <div className="modal-card-body">
          <Stack
            width={'100%'}
            direction="column"
            justifyContent="flex-start"
            alignItems="center"
            spacing={2}
          >
            {isLoading || isFetching
              ? <>
                <Skeleton variant='text' width={240} />
                <Skeleton variant='rounded' height={40} width={'100%'} />
                <Skeleton variant='rounded' height={40} width={'100%'} />
              </>
              : <>
                {t('Filling.fillSubitens.selectGeographicHierarchy')}
                <SpanColored
                  style={{
                    backgroundColor: !selectedUnidade ? '#F6D265' : '#31AD21',
                    color: !selectedUnidade ? 'black' : 'white'
                  }}
                >
                  {!results[1].data
                    ? `${t('Filling.fillSubitens.formNotAssociatedWithHierarchy')}`
                    : !selectedUnidade
                      ? `${t('Filling.fillSubitens.formExecutionRequiresHierarchy')}`
                      : `${t('Filling.fillSubitens.hierarchySelected')}`}
                </SpanColored>
                {results[1].data
                  && <AutocompleteSApi
                    setValue={setSelectedCliente}
                    arrayObj={results[1].data as any[]}
                    noOptionsLabel={`${t('Filling.fillSubitens.without')} ${hierarchyLabel.cliente} ${t('Filling.fillSubitens.associated')}`}
                    label={hierarchyLabel.cliente}
                    loadingText={`Buscando ${hierarchyLabel.cliente}...`}
                  />}

                {selectedCliente &&
                  <AutocompleteSApi
                    setValue={setSelectedLocal}
                    arrayObj={(results[1].data as any[])?.find((ele: { nome: string, id: string }) => ele.id === selectedCliente)?.locaisList}
                    noOptionsLabel={`${t('Filling.fillSubitens.without')} ${hierarchyLabel.local} ${t('Filling.fillSubitens.associated')}`}
                    label={hierarchyLabel.local}
                    loadingText={`${t('Filling.fillSubitens.searching')} ${hierarchyLabel.local}...`}
                  />}
                {selectedCliente &&
                  selectedLocal &&
                  <AutocompleteSApi
                    setValue={setSelectedConjunto}
                    arrayObj={(results[1].data as any[])
                      ?.find((ele: { nome: string, id: string }) => ele.id === selectedCliente)?.locaisList
                      ?.find((el: { nome: string, id: string }) => el?.id === selectedLocal)?.conjuntosList}
                    noOptionsLabel={`${t('Filling.fillSubitens.without')} ${hierarchyLabel.conjunto} ${t('Filling.fillSubitens.associated')}`}
                    label={hierarchyLabel.conjunto}
                    loadingText={`${t('Filling.fillSubitens.searching')} ${hierarchyLabel.conjunto}...`}
                  />}
                {selectedCliente
                  && selectedLocal
                  && selectedConjunto &&
                  <AutocompleteSApi
                    setValue={setSelectedUnidade}
                    arrayObj={(results[1].data as any[])
                      ?.find((ele: { nome: string, id: string }) => ele.id === selectedCliente)?.locaisList
                      ?.find((el: { nome: string, id: string }) => el?.id === selectedLocal)?.conjuntosList
                      ?.find((elem: { nome: string, id: string }) => elem.id === selectedConjunto).unidadesList}
                    noOptionsLabel={`${t('Filling.fillSubitens.without')} ${hierarchyLabel.unidade} ${t('Filling.fillSubitens.associated')}`}
                    label={hierarchyLabel.unidade}
                    loadingText={`${t('Filling.fillSubitens.searching')} ${hierarchyLabel.unidade}...`}
                  />}</>}
            {selectedUnidade
              && (results[0].data as any[]) &&
              <NewFormFilling form={(results[0].data as any)} openExecution={open} />
            }
          </Stack>
        </div>
        <FooterModal
          label={t('save')}
          disabled={!selectedUnidade}
          submit={submit.mutateAsync}
        />
      </div>
    </div>
  );
}